/**
 * Conversation Contract - the envelope every CONVERSATION.* payload carries
 *
 * Shared by every client app with a chat surface. Topics stay in appBroadcast.js
 * (one registry); this module owns the *payload* shape, the sink fan-out, and
 * the system prompt composition.
 *
 * The envelope:
 *
 *   {
 *     id,        // identifies one bubble - the canvas keys on this
 *     turnId,    // correlates a response back to the prompt that caused it
 *     role,      // 'user' | 'assistant'
 *     source,    // 'user' | 'system' | 'automation' - who spoke on whose behalf
 *     text,      // the content
 *     message,   // legacy alias for text (see below)
 *     timestamp, // display string, e.g. "10:32 AM" - what the bubble renders
 *     ts,        // epoch ms, for ordering
 *     phase,     // 'start' | 'delta' | 'end' | 'error' - stream lifecycle
 *     sinks,     // ['canvas', 'agent', ...] - where this goes
 *     directive, // { componentId, params } - machine instruction for acting sinks
 *     meta       // { model, systemHash, tokens, ... }
 *   }
 *
 * `text` and `directive` are the two halves the sink split exists for: one turn
 * can say something to the canvas and instruct the agent differently. A nav click
 * shows the user "Show me the dashboard" while telling the agent sink exactly
 * which view to render, with no natural-language round trip to re-derive it.
 *
 *   createTurn({
 *     text: 'Show me the dashboard',
 *     directive: { componentId: 'DASHBOARD', params: {} },
 *     sinks: [SINK.CANVAS, SINK.AGENT]
 *   })
 *
 * One turn, not two publishes: splitting them across topics mints two turnIds and
 * silently breaks the prompt/response correlation this envelope exists to carry.
 *
 * DIRECTIVE IS AN INSTRUCTION, META IS A RECORD. `directive` says what a sink
 * should do; `meta` describes what happened. Keeping them apart matters because
 * they point in opposite directions - a prompt carries an instruction forward, a
 * response carries a record back.
 *
 * A directive must NEVER reach the model transcript - record `text`, the words the
 * user saw. Machine instructions in the history teach the model to emit machine
 * instructions.
 *
 * Nothing is required of a publisher. normalizeTurn() accepts a bare string, a
 * legacy `{ message, timestamp }` payload, or a full envelope, and fills the
 * rest in. That is deliberate: hand-typed console publishes and VCR tapes
 * recorded before this contract existed must keep working.
 *
 *   PubSub.publish('CONVERSATION.PROMPT.AGENT', { message: 'what is a moose' })
 *
 * `message` is kept as a mirrored alias of `text` for the same reason - existing
 * subscribers that read `data.message` do not break.
 *
 * NOTE: no bare imports here - `shared/` sits outside the pnpm workspaces.
 */

import AppBroadcast from './appBroadcast.js';

export const ROLE = Object.freeze({
  USER: 'user',
  ASSISTANT: 'assistant'
});

/** Who originated the turn. A prompt the app sends for the user is 'automation'. */
export const SOURCE = Object.freeze({
  USER: 'user',
  SYSTEM: 'system',
  AUTOMATION: 'automation'
});

/** Stream lifecycle. A non-streamed turn is a single 'end'. */
export const PHASE = Object.freeze({
  START: 'start',
  DELTA: 'delta',
  END: 'end',
  ERROR: 'error'
});

/** Where a turn is delivered. Sinks compose - they are not mutually exclusive. */
export const SINK = Object.freeze({
  CANVAS: 'canvas',
  AGENT: 'agent',
  WORKFLOW: 'workflow',
  STAGE: 'stage'
});

/**
 * Topic sub-leaf -> default sinks.
 *
 * The leaf is a shorthand for a sink set, not a separate concept: AGENT_CANVAS
 * is simply both. An envelope carrying an explicit `sinks` array overrides this,
 * so a third sink never requires a new AGENT_CANVAS_WORKFLOW-style topic.
 */
const SUBTOPIC_SINKS = Object.freeze({
  CANVAS: [SINK.CANVAS],
  AGENT: [SINK.AGENT],
  AGENT_CANVAS: [SINK.CANVAS, SINK.AGENT],
  STAGE: [SINK.STAGE],
  WORKFLOW: [SINK.WORKFLOW]
});

export const TOPIC = Object.freeze({
  PROMPT: AppBroadcast.CONVERSATION_PROMPT,
  PROMPT_CANVAS: AppBroadcast.CONVERSATION_PROMPT_CANVAS,
  PROMPT_AGENT: AppBroadcast.CONVERSATION_PROMPT_AGENT,
  PROMPT_AGENT_CANVAS: AppBroadcast.CONVERSATION_PROMPT_AGENT_CANVAS,
  PROMPT_STAGE: AppBroadcast.CONVERSATION_PROMPT_STAGE,
  RESPONSE: AppBroadcast.CONVERSATION_RESPONSE,
  RESPONSE_CANVAS: AppBroadcast.CONVERSATION_RESPONSE_CANVAS,
  RESPONSE_WORKFLOW: AppBroadcast.CONVERSATION_RESPONSE_WORKFLOW
});

/** Prefix marking a client-side command rather than a model prompt. */
export const COMMAND_PREFIX = '::';

export function isCommand(text) {
  return typeof text === 'string' && text.trim().startsWith(COMMAND_PREFIX);
}

let idCounter = 0;

export function newId(prefix = 'msg') {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return `${prefix}_${globalThis.crypto.randomUUID()}`;
  }
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
}

/** The display string the bubble components render. */
export function displayTime(date = new Date()) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Extract the sub-leaf from a full topic: 'CONVERSATION.PROMPT.AGENT_CANVAS'
 * -> 'AGENT_CANVAS'. Returns '' for a bare parent topic, which is why sinks
 * fall back to a default rather than reading an index that may not exist.
 */
export function subTopicOf(topic) {
  if (typeof topic !== 'string') return '';
  const parts = topic.split('.');
  return parts.length > 2 ? parts.slice(2).join('.') : '';
}

/**
 * Build a well-formed envelope. Every field has a default, so callers supply
 * only what they know.
 */
export function createTurn(fields = {}) {
  const now = new Date();
  const text = fields.text ?? fields.message ?? '';

  const turn = {
    id: fields.id ?? newId(fields.role === ROLE.ASSISTANT ? 'res' : 'req'),
    turnId: fields.turnId ?? newId('turn'),
    role: fields.role ?? ROLE.USER,
    source: fields.source ?? SOURCE.USER,
    text,
    message: text,
    timestamp: fields.timestamp ?? displayTime(now),
    ts: fields.ts ?? now.getTime(),
    phase: fields.phase ?? PHASE.END,
    sinks: fields.sinks ?? [SINK.CANVAS],
    directive: fields.directive ?? null,
    meta: fields.meta ?? {}
  };

  return turn;
}

/**
 * The instruction a turn carries, or null.
 *
 * Read this rather than `turn.directive?.componentId` at call sites, so a
 * malformed directive (a bare string, say) fails in one place instead of
 * silently resolving to undefined in several.
 */
export function directiveOf(turn) {
  const d = turn?.directive;
  if (!d || typeof d !== 'object' || !d.componentId) return null;
  return {
    componentId: d.componentId,
    // Absent means "render the view"; present means "run this, then render it".
    action: d.action ?? null,
    params: d.params ?? {}
  };
}

/**
 * Coerce whatever a publisher sent into an envelope.
 *
 * @param {object|string} data - envelope, legacy `{message, timestamp}`, or a bare string
 * @param {object} [defaults]
 * @param {string} [defaults.role]
 * @param {string} [defaults.subTopic] - topic leaf, used to resolve sinks
 * @param {string} [defaults.source]
 * @returns {object} envelope
 */
export function normalizeTurn(data, defaults = {}) {
  const raw = typeof data === 'string' ? { text: data } : (data ?? {});
  const role = raw.role ?? defaults.role ?? ROLE.USER;

  // Legacy payloads used `streaming: true` to mean "update the open bubble".
  let phase = raw.phase;
  if (!phase) phase = raw.streaming ? PHASE.DELTA : PHASE.END;

  const sinks = Array.isArray(raw.sinks) && raw.sinks.length
    ? raw.sinks
    : sinksFor(defaults.subTopic, role);

  return createTurn({
    ...raw,
    role,
    phase,
    sinks,
    source: raw.source ?? defaults.source ?? SOURCE.USER
  });
}

/**
 * Resolve a sink set from a topic leaf, defaulting to the canvas - an unknown
 * leaf should still show the user something rather than vanish.
 */
export function sinksFor(subTopic, role = ROLE.USER) {
  if (subTopic && SUBTOPIC_SINKS[subTopic]) return SUBTOPIC_SINKS[subTopic];
  return role === ROLE.ASSISTANT ? [SINK.CANVAS] : [SINK.CANVAS];
}

/**
 * Dynamic system prompt composition.
 *
 * The system message is resolved per request and prepended at send time - it is
 * never stored in the transcript. Storing it there means changing it either has
 * no effect on an existing conversation or silently rewrites history the model
 * already saw (and invalidates the engine's prefix cache from position 0).
 *
 *   SystemPromptProvider.register('role', (ctx) =>
 *     ctx.entity?.class ? `The user's role is ${ctx.entity.class}.` : null)
 */
export const SystemPromptProvider = {
  base: 'You are a helpful AI assistant running locally in the browser using WebLLM. Be concise and helpful.',

  /** name -> (ctx) => string | null */
  contributors: new Map(),

  register(name, fn) {
    if (typeof fn !== 'function') throw new TypeError('[Conversation] contributor must be a function');
    this.contributors.set(name, fn);
    return this;
  },

  unregister(name) {
    this.contributors.delete(name);
    return this;
  },

  setBase(text) {
    this.base = text;
    return this;
  },

  /** Compose the system message for one request. A throwing contributor is skipped. */
  compose(ctx = {}) {
    const parts = [this.base];

    for (const [name, fn] of this.contributors) {
      try {
        const part = fn(ctx);
        if (part) parts.push(String(part));
      } catch (err) {
        console.warn(`[Conversation] system prompt contributor "${name}" failed`, err);
      }
    }

    return parts.filter(Boolean).join('\n\n');
  },

  /**
   * Short stable digest, stamped onto turn.meta.systemHash so a transcript (or
   * a VCR tape) records which instructions produced a given answer.
   */
  hash(text) {
    let h = 5381;
    for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }
};

export default {
  ROLE,
  SOURCE,
  PHASE,
  SINK,
  TOPIC,
  COMMAND_PREFIX,
  isCommand,
  newId,
  displayTime,
  subTopicOf,
  createTurn,
  normalizeTurn,
  directiveOf,
  sinksFor,
  SystemPromptProvider
};
